import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { History, Eye, RotateCcw, RefreshCw } from 'lucide-react';

interface HistoryEntry {
  id: string;
  message_content: string;
  changed_by_email: string;
  change_reason: string | null;
  is_active: boolean;
  character_count: number;
  version_number: number;
  created_at: string;
}

interface SystemMessageHistoryProps {
  onMessageReverted: (content: string) => void;
}

const SystemMessageHistory: React.FC<SystemMessageHistoryProps> = ({ onMessageReverted }) => {
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEntry, setSelectedEntry] = useState<HistoryEntry | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [reverting, setReverting] = useState(false);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('system_message_history')
        .select('*')
        .order('version_number', { ascending: false });

      if (error) throw error;

      setHistory(data || []);
    } catch (error) {
      console.error('Error loading system message history:', error);
      toast.error('Failed to load message history');
    } finally {
      setLoading(false);
    }
  };

  const handleViewMessage = (entry: HistoryEntry) => {
    setSelectedEntry(entry);
    setViewDialogOpen(true);
  };

  const handleRevertToVersion = async (entry: HistoryEntry) => {
    setReverting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Get current max version number
      const { data: maxVersionData, error: maxVersionError } = await supabase
        .from('system_message_history')
        .select('version_number')
        .order('version_number', { ascending: false })
        .limit(1)
        .single();

      if (maxVersionError) throw maxVersionError;

      const nextVersion = (maxVersionData?.version_number || 0) + 1;

      // Mark all versions as inactive
      const { error: deactivateError } = await supabase
        .from('system_message_history')
        .update({ is_active: false })
        .eq('is_active', true);

      if (deactivateError) throw deactivateError;

      // Insert new version with reverted content
      const { error: insertError } = await supabase
        .from('system_message_history')
        .insert({
          message_content: entry.message_content,
          changed_by_user_id: user.id,
          changed_by_email: user.email || '',
          change_reason: `Reverted to version ${entry.version_number}`,
          is_active: true,
          character_count: entry.message_content.length,
          version_number: nextVersion
        });

      if (insertError) throw insertError;

      // Update app_settings for backward compatibility
      const { error: updateError } = await supabase
        .from('app_settings')
        .update({ 
          value: entry.message_content,
          updated_at: new Date().toISOString()
        })
        .eq('key', 'mcp_system_message');

      if (updateError) throw updateError;

      toast.success(`Successfully reverted to version ${entry.version_number}`);
      onMessageReverted(entry.message_content);
      await loadHistory();
    } catch (error) {
      console.error('Error reverting message:', error);
      toast.error('Failed to revert to selected version');
    } finally {
      setReverting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <RefreshCw className="h-6 w-6 animate-spin mr-2" />
          Loading message history...
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            System Message History
          </CardTitle>
          <CardDescription>
            View and manage previous versions of the system message. You can revert to any previous version.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Version</TableHead>
                  <TableHead>Changed By</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Length</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[150px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.length > 0 ? (
                  history.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="font-medium">v{entry.version_number}</TableCell>
                      <TableCell>{entry.changed_by_email}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {format(new Date(entry.created_at), 'MMM d, yyyy HH:mm')}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        {entry.change_reason || '-'}
                      </TableCell>
                      <TableCell>{entry.character_count.toLocaleString()} chars</TableCell>
                      <TableCell>
                        {entry.is_active ? (
                          <Badge variant="default">Active</Badge>
                        ) : (
                          <Badge variant="secondary">Historical</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewMessage(entry)}
                            className="flex items-center gap-1"
                          >
                            <Eye className="h-3 w-3" />
                            View
                          </Button>
                          {!entry.is_active && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="flex items-center gap-1"
                                  disabled={reverting}
                                >
                                  <RotateCcw className="h-3 w-3" />
                                  Revert
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Revert to Version {entry.version_number}?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will create a new version with the content from version {entry.version_number}.
                                    The current system message will be replaced immediately.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleRevertToVersion(entry)}
                                    disabled={reverting}
                                  >
                                    {reverting ? 'Reverting...' : 'Revert'}
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No history entries found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* View Message Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>
              System Message - Version {selectedEntry?.version_number}
              {selectedEntry?.is_active && <Badge className="ml-2">Active</Badge>}
            </DialogTitle>
            <DialogDescription>
              Changed by {selectedEntry?.changed_by_email} on{' '}
              {selectedEntry && format(new Date(selectedEntry.created_at), 'MMM d, yyyy HH:mm')}
              {selectedEntry?.change_reason && (
                <span className="block mt-1">
                  <strong>Reason:</strong> {selectedEntry.change_reason}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={selectedEntry?.message_content || ''}
              readOnly
              className="min-h-[400px] font-mono text-sm"
            />
            <div className="text-sm text-muted-foreground">
              {selectedEntry?.character_count.toLocaleString()} characters
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SystemMessageHistory;